<script lang="ts">
	/**
	 * Command Palette Component
	 *
	 * Wraps svelte-command-palette with Rool theming and integration.
	 * Uses the showCommandPalette store for visibility control.
	 * Supports mode-aware filtering to show contextually relevant commands.
	 */
	import { get } from 'svelte/store';
	import CommandPaletteComponent, { paletteStore } from 'svelte-command-palette';
	import type { action as PaletteAction } from 'svelte-command-palette';
	import { showCommandPalette, canvasEditor, nodes, selectedNodeIds } from '../../stores';
	import {
		buildPaletteActions,
		getCurrentModeDisplay,
		getModeStack,
	} from '../../commands/bridge';
	import type { UINode } from '@rool/renderer';

	// Reactive state
	let allActions: PaletteAction[] = $state([]);
	let isVisible = $state(false);
	let currentModeDisplay = $state<string | null>(null);
	let modeStack = $state<string[]>(['default']);

	// Search query from the library's palette store
	let searchQuery = $derived($paletteStore.textInput || '');

	// Filter actions ourselves to work around the library's broken search.
	// The library's internal handleSearch has a Svelte 5 timing issue where
	// bind:value and oninput race, so $paletteStore.textInput is stale when
	// the library reads it. We bypass this by filtering before passing commands.
	let actions = $derived.by(() => {
		if (!searchQuery) return allActions;
		const q = searchQuery.toLowerCase();
		return allActions.filter(
			(a) =>
				a.title?.toLowerCase().includes(q) ||
				a.subTitle?.toLowerCase().includes(q) ||
				a.description?.toLowerCase().includes(q) ||
				a.keywords?.some((k: string) => k?.toLowerCase().includes(q))
		);
	});

	// Track mode changes
	$effect(() => {
		const editor = get(canvasEditor);
		if (!editor) return;

		const input = editor.getInputSystem();

		// Check mode periodically while palette is visible
		// This ensures we catch mode changes
		const checkMode = () => {
			const newStack = input.getStack();
			if (JSON.stringify(newStack) !== JSON.stringify(modeStack)) {
				modeStack = newStack;
				currentModeDisplay = getCurrentModeDisplay(input);
			}
		};

		// Check immediately
		checkMode();

		// Set up interval for tracking (only when visible)
		if (isVisible) {
			const interval = setInterval(checkMode, 100);
			return () => clearInterval(interval);
		}
	});

	// Rebuild actions when visibility or mode changes
	$effect(() => {
		const editor = get(canvasEditor);
		if (!editor) {
			allActions = [];
			return;
		}

		const input = editor.getInputSystem();
		const currentNodes = get(nodes);
		const selected = get(selectedNodeIds);
		const selectedNodes = currentNodes.filter((n) => selected.has(n.id));

		// Track modeStack/isVisible to rebuild on mode changes and palette open
		void modeStack;
		void isVisible;

		// Build from command registry (includes all registerCommand() entries)
		// plus InputSystem shortcut display strings
		allActions = buildPaletteActions(input, selectedNodes);
	});

	// Sync visibility with store
	$effect(() => {
		isVisible = $showCommandPalette;
	});

	// Handle close
	function handleClose() {
		showCommandPalette.set(false);
	}

	// Handle open
	function handleOpen() {
		showCommandPalette.set(true);
	}

	// Sync palette store visibility with our store
	$effect(() => {
		if (isVisible !== $paletteStore.isVisible) {
			if (isVisible) {
				// We want to open but palette is closed
				// The palette will open via shortcut or we need to trigger it
			} else if ($paletteStore.isVisible) {
				// Palette is open but we want it closed
				// This happens when user clicks outside or presses Escape
				showCommandPalette.set(false);
			}
		}
	});

	// Watch palette store for external closes (Escape, click outside)
	$effect(() => {
		if (!$paletteStore.isVisible && $showCommandPalette) {
			showCommandPalette.set(false);
		}
	});
</script>

<!-- Mode indicator positioned near the palette -->
{#if isVisible && currentModeDisplay}
	<div class="palette-extras">
		<div class="palette-mode-header">
			<span class="mode-name">{currentModeDisplay}</span>
		</div>
	</div>
{/if}

<CommandPaletteComponent
	commands={actions}
	placeholder="Search commands..."
	shortcut="$mod+k"
	onOpen={handleOpen}
	onClose={handleClose}
	unstyled={false}
	inputClass="palette-input"
	overlayClass="palette-overlay"
	paletteWrapperInnerClass="palette-wrapper"
	resultsContainerClass="palette-results"
	resultContainerClass="palette-result"
	optionSelectedClass="palette-result-selected"
	titleClass="palette-title"
	subtitleClass="palette-subtitle"
	descriptionClass="palette-description"
	keyboardButtonClass="palette-kbd"
/>

<style>
	/* Command palette theming */
	:global(.palette-overlay) {
		background: rgba(0, 0, 0, 0.5);
		backdrop-filter: blur(4px);
	}

	:global(.palette-wrapper) {
		background: var(--bg1, #ffffff);
		border: 1px solid var(--border, #e5e5e5);
		border-radius: 12px;
		box-shadow:
			0 20px 25px -5px rgba(0, 0, 0, 0.1),
			0 10px 10px -5px rgba(0, 0, 0, 0.04);
		max-width: 560px;
		width: 100%;
		overflow: hidden;
	}

	:global(.palette-input) {
		background: transparent;
		border: none;
		border-bottom: 1px solid var(--border, #e5e5e5);
		color: var(--text, #1a1a1a);
		font-size: 16px;
		padding: 16px;
		width: 100%;
		outline: none;
	}

	:global(.palette-input::placeholder) {
		color: var(--text-muted, #666666);
	}

	:global(.palette-results) {
		max-height: 400px;
		overflow-y: auto;
		padding: 8px;
	}

	:global(.palette-result) {
		align-items: center;
		border-radius: 8px;
		cursor: pointer;
		display: flex;
		gap: 12px;
		padding: 10px 12px;
		transition: background 0.1s ease;
	}

	:global(.palette-result:hover),
	:global(.palette-result-selected) {
		background: var(--bg2, #f5f5f5);
	}

	:global(.palette-title) {
		color: var(--text, #1a1a1a);
		font-size: 14px;
		font-weight: 500;
	}

	:global(.palette-subtitle) {
		color: var(--text-muted, #666666);
		font-size: 12px;
	}

	:global(.palette-description) {
		color: var(--text-muted, #666666);
		font-size: 12px;
		margin-top: 2px;
	}

	:global(.palette-kbd) {
		background: var(--bg2, #f5f5f5);
		border: 1px solid var(--border, #e5e5e5);
		border-radius: 4px;
		color: var(--text-muted, #666666);
		font-family: ui-monospace, monospace;
		font-size: 11px;
		padding: 2px 6px;
	}

	/* Dark theme */
	:global(.dark .palette-wrapper) {
		background: var(--bg1, #262626);
		border-color: var(--border, #404040);
	}

	:global(.dark .palette-input) {
		border-bottom-color: var(--border, #404040);
		color: var(--text, #ffffff);
	}

	:global(.dark .palette-result:hover),
	:global(.dark .palette-result-selected) {
		background: var(--bg2, #333333);
	}

	:global(.dark .palette-title) {
		color: var(--text, #ffffff);
	}

	:global(.dark .palette-kbd) {
		background: var(--bg2, #333333);
		border-color: var(--border, #404040);
	}

	/* Mode header and show all toggle */
	.palette-extras {
		position: fixed;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		/* Position below the palette (palette max-width is 560px, ~300px height estimated) */
		margin-top: 230px;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 8px;
		z-index: 10001; /* Above palette overlay */
		pointer-events: auto;
	}

	.palette-mode-header {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 6px 12px;
		background: var(--bg1, #ffffff);
		border: 1px solid var(--border, #e5e5e5);
		border-radius: 6px;
		font-size: 12px;
		font-weight: 500;
		color: var(--text-muted, #666666);
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	}

	/* Dark theme for extras */
	:global(.dark) .palette-mode-header {
		background: var(--bg1, #262626);
		border-color: var(--border, #404040);
		color: var(--text-muted, #999999);
	}
</style>
